import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';

// TypeScript interfaces
interface ShopifyStore {
  id: number;
  storeIdentifier: string;
  storeName: string;
  shopUrl: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface FormData {
  storeName: string;
  shopUrl: string;
  accessToken: string;
  setAsActive: boolean;
}

// Helper function for error handling
const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { data?: { error?: string; message?: string } }; message?: string };
    if (axiosError.response?.data?.error) {
      return axiosError.response.data.error;
    }
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }
    if (axiosError.message) {
      return axiosError.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unknown error occurred';
};

const ShopifyMultiStoreSection: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  // State
  const [stores, setStores] = useState<ShopifyStore[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [selectedStore, setSelectedStore] = useState<ShopifyStore | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<FormData>({
    storeName: '',
    shopUrl: '',
    accessToken: '',
    setAsActive: false
  });

  // Fetch stores on mount
  useEffect(() => {
    fetchStores();
  }, []);

  // Fetch all stores
  const fetchStores = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await api.get('/api/shopify/stores');
      setStores(response.data.stores || []);
    } catch (error: unknown) {
      console.error('Failed to fetch stores:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch stores';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle add store
  const handleAddStore = async (): Promise<void> => {
    if (!formData.storeName || !formData.shopUrl || !formData.accessToken) {
      enqueueSnackbar('Please fill in all fields', { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/shopify/stores', formData);
      enqueueSnackbar('Store added successfully', { variant: 'success' });
      setAddDialogOpen(false);
      resetForm();
      fetchStores();
    } catch (error: unknown) {
      console.error('Failed to add store:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add store';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle update store
  const handleUpdateStore = async (): Promise<void> => {
    if (!selectedStore) return;

    setLoading(true);
    try {
      await api.put(`/api/shopify/stores/${selectedStore.storeIdentifier}`, {
        storeName: formData.storeName,
        shopUrl: formData.shopUrl,
        accessToken: formData.accessToken || undefined
      });
      enqueueSnackbar('Store updated successfully', { variant: 'success' });
      setEditDialogOpen(false);
      resetForm();
      fetchStores();
    } catch (error: unknown) {
      console.error('Failed to update store:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update store';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle activate store
  const handleActivateStore = async (storeIdentifier: string): Promise<void> => {
    setLoading(true);
    try {
      await api.put(`/api/shopify/stores/${storeIdentifier}/activate`);
      enqueueSnackbar('Store activated successfully', { variant: 'success' });
      fetchStores();
    } catch (error: unknown) {
      console.error('Failed to activate store:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to activate store';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete store
  const handleDeleteStore = async (storeIdentifier: string, storeName: string): Promise<void> => {
    if (!window.confirm(`Are you sure you want to delete store "${storeName}"?`)) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/api/shopify/stores/${storeIdentifier}`);
      enqueueSnackbar('Store deleted successfully', { variant: 'success' });
      fetchStores();
    } catch (error: unknown) {
      console.error('Failed to delete store:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete store';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle sync store
  const handleSyncStore = async (storeIdentifier: string, storeName: string): Promise<void> => {
    setLoading(true);
    try {
      const response = await api.post(`/api/shopify/stores/${storeIdentifier}/sync`, {
        forceRefresh: true
      });
      enqueueSnackbar(
        `Synced ${response.data.ordersProcessed} orders from "${storeName}"`,
        { variant: 'success' }
      );
    } catch (error: unknown) {
      console.error('Failed to sync store:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to sync store';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Open edit dialog
  const openEditDialog = (store: ShopifyStore): void => {
    setSelectedStore(store);
    setFormData({
      storeName: store.storeName,
      shopUrl: store.shopUrl,
      accessToken: '', // Don't pre-fill for security
      setAsActive: false
    });
    setEditDialogOpen(true);
  };

  // Reset form
  const resetForm = (): void => {
    setFormData({
      storeName: '',
      shopUrl: '',
      accessToken: '',
      setAsActive: false
    });
    setSelectedStore(null);
  };

  return (
    <Paper sx={{ p: 3 }} elevation={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Shopify Stores
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          disabled={loading}
        >
          Add Store
        </Button>
      </Box>

      {stores.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No Shopify stores connected. Click "Add Store" to get started.
        </Alert>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Store Name</TableCell>
                <TableCell>Shop URL</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stores.map((store) => (
                <TableRow key={store.storeIdentifier}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StoreIcon color="primary" />
                      <Typography variant="body1" fontWeight={store.isActive ? 600 : 400}>
                        {store.storeName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {store.shopUrl}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {store.isActive ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Active"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip
                        label="Inactive"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      {!store.isActive && (
                        <Tooltip title="Set as active store">
                          <IconButton
                            size="small"
                            onClick={() => handleActivateStore(store.storeIdentifier)}
                            disabled={loading}
                            color="primary"
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Sync orders">
                        <IconButton
                          size="small"
                          onClick={() => handleSyncStore(store.storeIdentifier, store.storeName)}
                          disabled={loading}
                        >
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit store">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(store)}
                          disabled={loading}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete store">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteStore(store.storeIdentifier, store.storeName)}
                          disabled={loading || store.isActive}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Store Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Shopify Store</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Store Name"
                value={formData.storeName}
                onChange={(e) => setFormData({ ...formData, storeName: e.target.value })}
                fullWidth
                placeholder="My Store"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Shop URL"
                value={formData.shopUrl}
                onChange={(e) => setFormData({ ...formData, shopUrl: e.target.value })}
                fullWidth
                placeholder="your-store.myshopify.com"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Access Token"
                value={formData.accessToken}
                onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
                fullWidth
                type="password"
                placeholder="shpat_..."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.setAsActive}
                    onChange={(e) => setFormData({ ...formData, setAsActive: e.target.checked })}
                  />
                }
                label="Set as active store"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddDialogOpen(false); resetForm(); }}>
            Cancel
          </Button>
          <Button onClick={handleAddStore} variant="contained" disabled={loading}>
            Add Store
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Store Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Store</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Store Name"
                value={formData.storeName}
                onChange={(e) => setFormData({ ...formData, storeName: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Shop URL"
                value={formData.shopUrl}
                onChange={(e) => setFormData({ ...formData, shopUrl: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Access Token (leave empty to keep current)"
                value={formData.accessToken}
                onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
                fullWidth
                type="password"
                placeholder="Leave empty to keep current token"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); resetForm(); }}>
            Cancel
          </Button>
          <Button onClick={handleUpdateStore} variant="contained" disabled={loading}>
            Update Store
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ShopifyMultiStoreSection;
